import { useMemo } from 'react';

import { useQuery } from '@mint-lib/query-client';
import { Categories, Tags } from '@mint-sdk/api-tags';

type UseTagsParams = {
  isMint2?: boolean;
  categoryType?: string;
};

const setCategoryLabel = (nodesMap: Map<string, any>) => {
  nodesMap.forEach((node) => {
    if (node.children?.length > 0) {
      const categoryName = node.children[0].categoryName;
      node.children = [{ name: categoryName, id: node.name }, ...node.children];
      setCategoryLabel(node.children);
    }
  });
};
export const useTags = ({ isMint2, categoryType }: UseTagsParams) => {
  // MINT2.0
  const treeData = useQuery({
    ...Tags.treeDataQueryOptions({
      params: {},
    }),
    enabled: isMint2,
  });

  //MINT
  const tagsOld = useQuery({
    ...Tags.listOldQueryOptions({
      params: {
        type: categoryType,
      },
    }),
    enabled: !isMint2,
  });

  const categoriesOld = useQuery({
    ...Categories.listOldQueryOptions({
      params: {
        type: categoryType,
      },
    }),
    enabled: !isMint2,
  });
  const industries = useQuery({
    ...Tags.industriesListOldQueryOptions({
      params: {},
    }),
    enabled: !isMint2 && categoryType === 'brands',
  });

  const visibleTags = useMemo(() => {
    const tags = treeData.data
      ?.flatMap((obj) => Object.values(obj))
      .find((hierarhy) => hierarhy.name === categoryType)?.tags;
    return tags || [];
  }, [treeData.data, categoryType]);

  // Construct tree data structure (if - MINT2.0 and else - MINT)
  const nodesMap = new Map();

  if (isMint2) {
    // set first level category label
    nodesMap.set('uncategorized', {
      name: visibleTags[0]?.categoryName || 'Uncategorized',
      categoryType: 'Uncategorized',
      level: 0,
      children: [],
    });
    // set disabled property for planning scenario based on assigned tags and user tags
    visibleTags?.forEach((tag) => {
      nodesMap.set(tag.id, {
        ...tag,
        category: tag.categoryName || '',
        categoryType,
      });
    });

    // provide category labels for tags based on their children categoryName property
    setCategoryLabel(nodesMap);
  } else {
    const categoryMap = new Map(categoriesOld.data?.map((c) => [c.id, c.name]));
    const industryMap = new Map(industries.data?.map((i) => [i.id, i.name]));
    // set first level category label
    const firstLevelCategory =
      Object.values(categoriesOld.data || {}).find(
        (category1) => category1.level === 1 && category1.type === categoryType,
      )?.name || '';
    nodesMap.set('uncategorized', {
      name: firstLevelCategory,
      category: 'Uncategorized',
      categoryType: 'Uncategorized',
      level: 0,
      children: [],
    });
    tagsOld.data?.forEach((tag) => {
      return nodesMap.set(tag.id, {
        id: tag.id,
        name: tag.name,
        category: tag.category ? (categoryMap.get(tag.category) ?? null) : null,
        industry: tag.industry ? (industryMap.get(tag.industry) ?? null) : null,
        avatar: tag.avatar,
        users_count: tag.usersCount,
        parent: tag.parent,
        children: [],
      });
    });

    nodesMap.forEach((node) => {
      if (node.parent) {
        const parent = nodesMap.get(node.parent);
        if (parent) {
          parent.children.push(node);
        }
      }
    });

    nodesMap.forEach((node: { children: any[]; name: string }) => {
      if (node.children?.length > 0) {
        const categoryName = node.children[0].category;
        node.children = [{ name: categoryName }, ...node.children];
      }
    });
  }

  const visibleCategoryEntries = useMemo(() => {
    return (
      treeData.data
        ?.flatMap((obj) => Object.values(obj))
        .map((hierarhy) => [hierarhy.name, hierarhy] as const) || []
    );
  }, [treeData]);

  // tabs are used in LabelModal to filter tags by categoryType
  const tabs = visibleCategoryEntries.map(([id, value]) => ({
    id,
    name: value.name,
  }));

  return {
    tags: visibleTags || [],
    tabs,
    isLoading:
      treeData.isLoading ||
      tagsOld.isLoading ||
      categoriesOld.isLoading ||
      industries.isLoading,
    isRefetching: treeData.isRefetching,
    refetch: treeData.refetch,
    nodesMap: Array.from(nodesMap?.values() ?? []),
  };
};
